<template>
    <ActionDialog
        v-model="show_action_dialog"
        :persistent="is_mobile_number_only_flow"
        :show_header="false"
        @after-leave="after_leave"
    >
        <template #content>
            <ProfileDialogContactInfoPage
                v-if="page === 1"
                v-model:email="email"
                v-model:is_email_valid="is_email_valid"
                v-model:is_mobile_number_valid="is_mobile_number_valid"
                v-model:mobile_number="mobile_number"
                :is_mobile_number_only_flow="is_mobile_number_only_flow"
            />
            <ProfileDialogBankAccountInfoPage
                v-if="page === 2 && !is_mobile_number_only_flow"
                v-model:account_number="account_number"
                v-model:clearing_number="clearing_number"
                v-model:is_account_number_valid="is_account_number_valid"
                v-model:is_clearing_number_valid="is_clearing_number_valid"
            />
            <div class="d-flex pt-2 px-6 pb-4">
                <v-spacer />
                <v-btn
                    @click="close_dialog(false)"
                    class="mr-2"
                    variant="text"
                >
                    {{ translate(!is_mobile_number_only_flow ? 'common.skip' : 'common.cancel') }}
                </v-btn>
                <v-btn
                    :disabled="is_button_disabled"
                    @click="handle_click"
                    color="primary"
                    variant="flat"
                >
                    {{ translate(page === 1 && !is_mobile_number_only_flow ? 'common.next' : 'common.send_in') }}
                </v-btn>
            </div>
        </template>
    </ActionDialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { api_mixin } from '@old/mixins/api_mixin'
import { utils_mixin } from '@old/mixins/utils_mixin'
import { locales_mixin } from '@old/mixins/locales_mixin'
import { rules_mixin } from '@old/mixins/rules_mixin'
import ActionDialog from '@old/components/action_dialog/index.vue'
import ProfileDialogContactInfoPage from '@old/components/profile_dialog/ContactInfo.vue'
import ProfileDialogBankAccountInfoPage from '@old/components/profile_dialog/BankAccountInfo.vue'

export default {
    name: 'ProfileDialog',
    props: {
        context: {
            type: String,
        },
        document: {
            type: Object,
        },
    },
    mixins: [api_mixin, locales_mixin, rules_mixin, utils_mixin],
    components: { ProfileDialogBankAccountInfoPage, ProfileDialogContactInfoPage, ActionDialog },
    data() {
        return {
            show_action_dialog: false,
            page: 1,
            email: '',
            is_email_valid: true,
            mobile_number: '',
            is_mobile_number_valid: true,
            clearing_number: '',
            is_clearing_number_valid: true,
            account_number: '',
            is_account_number_valid: true,
        }
    },
    computed: {
        is_mobile_number_only_flow() {
            return this.context === 'request_invoice_copy'
        },
        is_button_disabled() {
            if (this.page === 1) {
                if (this.is_mobile_number_only_flow) {
                    return !this.is_mobile_number_valid
                }
                return !this.email || !this.is_email_valid || !this.mobile_number || !this.is_mobile_number_valid
            }
            return !this.is_clearing_number_valid || !this.is_account_number_valid
        },
        ...mapState(['user']),
        ...mapGetters(['token_json']),
    },
    watch: {
        'user._id': {
            handler(value) {
                if (!value) return
                this.email = this.deep_get(this.user, 'email_data.email', '')
                this.mobile_number = this.deep_get(this.user, 'mobile_number_data.mobile_number', '')
                this.clearing_number = this.deep_get(
                    this.user,
                    'payout_details.bank_account_number.clearing_number',
                    ''
                )
                this.account_number = this.deep_get(this.user, 'payout_details.bank_account_number.account_number', '')

                if (!this.is_mobile_number_only_flow && this.email && this.mobile_number) {
                    this.page = 2
                } else {
                    this.page = 1
                }
            },
            immediate: true,
        },
    },
    methods: {
        handle_click() {
            if (this.page === 1 && !this.is_mobile_number_only_flow) {
                this.page++
            } else {
                this.close_dialog(true)
            }
        },
        async close_dialog(should_save) {
            if (should_save) {
                await this.save_data()
            }
            await this.set_state_property({ property: 'dont_show_profile_dialog', data: true })
            // We still set it here so that if the user refreshes the page we can pick it up in App.vue
            sessionStorage.setItem('dont_show_profile_dialog', 'true')
            this.show_action_dialog = false
        },
        async save_data() {
            const updates = {}
            if (this.is_mobile_number_valid) {
                if (this.mobile_number !== this.deep_get(this.user, 'mobile_number_data.mobile_number', '')) {
                    updates['mobile_number_data'] = {
                        mobile_number: this.mobile_number.replaceAll(' ', ''),
                        verified: false,
                    }
                }
            }
            if (this.is_email_valid) {
                if (this.email !== this.deep_get(this.user, 'email_data.email', '')) {
                    updates['email_data'] = {
                        email: this.email,
                        verified: false,
                    }
                }
            }
            if (this.is_clearing_number_valid && this.is_account_number_valid) {
                let update_payout_details = false
                const payout_details = {
                    ...(this.user['payout_details'] || {}),
                }
                if (!payout_details['bank_account_number']) {
                    payout_details['bank_account_number'] = {}
                }
                if (
                    this.clearing_number !==
                    this.deep_get(this.user, 'payout_details.bank_account_number.clearing_number', '')
                ) {
                    payout_details['bank_account_number']['clearing_number'] = this.clearing_number
                    update_payout_details = true
                }
                if (
                    this.account_number !==
                    this.deep_get(this.user, 'payout_details.bank_account_number.account_number', '')
                ) {
                    payout_details['bank_account_number']['account_number'] = this.account_number
                    update_payout_details = true
                }

                if (update_payout_details) {
                    updates['payout_details'] = payout_details
                }
            }

            if (Object.keys(updates).length) {
                try {
                    const response = await this.api_patch({
                        url: `/customer--users/${this.user['_id']}`,
                        if_match: this.user['_etag'],
                        data: updates,
                    })
                    const user = {
                        ...this.user,
                        ...response.data,
                        ...updates,
                    }
                    await this.set_state_property({
                        property: 'user',
                        data: user,
                    })
                } catch (e) {
                    console.error(e)
                }
            }
        },
        async after_leave() {
            await this.close_dialog(false)
            await this.set_state_property({
                property: 'profile_dialog',
                data: null,
            })
        },
        ...mapActions(['set_state_property']),
    },
    beforeCreate() {},
    created() {},
    beforeMount() {},
    mounted() {
        this.show_action_dialog = true
    },
    beforeUpdate() {},
    updated() {},
    beforeUnmount() {},
    unmounted() {},
}
</script>

<style scoped lang="sass"></style>
